/**
 * Filing's controls (slice 6d spec §4.4, §8.3; Paper A2b, A2c): a list's Area
 * filter, a chip row on a phone and a menu on a desktop, kept in the URL as
 * `?area=<slug>|none`; and the Area sheet, which moves a chat, a task, a doc
 * or a routine to another Area, a bottom sheet on a phone and a menu on a
 * desktop.
 */
import { Button } from "@crew/ui/components/button";
import { ChatIcon } from "@crew/ui/components/chat-icon";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, CircleDashed, Layers, Plus } from "lucide-react";
import { useState, type ReactElement } from "react";
import { api, errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import type { Area, AreaRef } from "./api";
import { NewAreaWindow } from "./area-form";
import { AreaLock } from "./parts";

// ── The filter ──────────────────────────────────────────────────────────────

type Option = { value: string | null; label: string; icon: string | null };

/**
 * An archived Area's label, for a list filtered by its slug (it isn't in
 * `/api/me`'s Areas). Read only then.
 */
function useArchivedLabel(slug: string | null, known: boolean) {
  const archived = useQuery({
    queryKey: ["areas", "archived"],
    queryFn: () => api<{ areas: Area[] }>("GET", "/api/areas?archived=1"),
    enabled: Boolean(slug) && !known,
  });
  return archived.data?.areas.find((a) => a.slug === slug) ?? null;
}

function optionsFor(me: Pick<Me, "areas">, value: string | null, archived: Area | null): Option[] {
  const live: Option[] = me.areas.map((a) => ({ value: a.slug, label: a.label, icon: a.icon }));
  const gone =
    value && value !== "none" && !me.areas.some((a) => a.slug === value)
      ? [{ value, label: archived?.label ?? value, icon: archived?.icon ?? null }]
      : [];
  return [
    { value: null, label: "All areas", icon: null },
    ...live,
    ...gone,
    { value: "none", label: "No area", icon: null },
  ];
}

function OptionIcon({ option, size }: { option: Option; size: number }) {
  if (option.icon) return <ChatIcon name={option.icon} size={size} />;
  if (option.value === "none") return <CircleDashed aria-hidden style={{ width: size }} />;
  return <Layers aria-hidden style={{ width: size }} />;
}

/**
 * The Area filter (spec §4.4): "All areas", each Area and "No area". Shown
 * once the workspace has an Area, or the URL names one.
 */
export function AreaFilter({
  me,
  value,
  onChange,
  className,
}: {
  me: Pick<Me, "areas">;
  value: string | null;
  onChange: (area: string | null) => void;
  className?: string;
}) {
  const known = !value || value === "none" || me.areas.some((a) => a.slug === value);
  const archived = useArchivedLabel(value, known);
  if (me.areas.length === 0 && !value) return null;
  const options = optionsFor(me, value, archived);
  const chosen = options.find((o) => o.value === value) ?? options[0]!;
  return (
    <div data-area-filter={value ?? "all"} className={className}>
      {/* A phone's: chips that scroll sideways. */}
      <div
        role="radiogroup"
        aria-label="Area"
        className="-mx-4 flex gap-2 overflow-x-auto px-4 [scrollbar-width:none] md:hidden"
      >
        {options.map((o) => {
          const on = o.value === value;
          return (
            <button
              key={o.value ?? "all"}
              type="button"
              role="radio"
              aria-checked={on}
              data-area-option={o.value ?? "all"}
              onClick={() => onChange(o.value)}
              className={cn(
                "flex h-8 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium",
                on
                  ? "bg-foreground text-background"
                  : "bg-card text-copy ring-1 ring-input ring-inset",
              )}
            >
              {o.icon ? <ChatIcon name={o.icon} size={14} /> : null}
              {o.label}
            </button>
          );
        })}
      </div>
      {/* A desktop's: a menu. */}
      <div className="hidden md:block">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={<Button variant="outline" className="shrink-0" data-area-filter-menu />}
          >
            <OptionIcon option={chosen} size={16} />
            {chosen.label}
            <ChevronDown className="text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-52">
            <DropdownMenuRadioGroup
              value={value ?? "all"}
              onValueChange={(v) => onChange(v === "all" ? null : (v as string))}
            >
              {options.map((o) => (
                <DropdownMenuRadioItem
                  key={o.value ?? "all"}
                  value={o.value ?? "all"}
                  data-area-option={o.value ?? "all"}
                >
                  <OptionIcon option={o} size={16} />
                  {o.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

// ── The Area sheet ──────────────────────────────────────────────────────────

type Choice = {
  id: string | null;
  label: string;
  icon: string | null;
  archived: boolean;
  /** Just you (slice 6g §8.2): its lock, and a line on what moving in means. */
  private?: boolean;
  /** Greyed: a private Area's things don't come out (§4.3). */
  shut?: boolean;
};

/**
 * The live Areas, the current one first when it's archived, then No area.
 * In a private Area, it comes first and nothing else can be picked but
 * another private one (slice 6g §4.3, Paper P3b).
 */
export function choicesFor(me: Pick<Me, "areas">, current: AreaRef | null): Choice[] {
  const live: Choice[] = me.areas.map((a) => ({
    id: a.id,
    label: a.label,
    icon: a.icon,
    archived: false,
    private: a.private === true,
  }));
  const gone: Choice[] =
    current && !me.areas.some((a) => a.id === current.id)
      ? [{ id: current.id, label: current.label, icon: current.icon, archived: true }]
      : [];
  const all = [...gone, ...live, { id: null, label: "No area", icon: null, archived: false }];
  if (!current?.private) return all;
  const here = all.filter((c) => c.id === current.id);
  const rest = all.filter((c) => c.id !== current.id).map((c) => ({ ...c, shut: !c.private }));
  return [...here, ...rest];
}

/**
 * P3b's title and line, in a private Area: "This chat stays in Company
 * finance". When another private Area of theirs can take it, the title says
 * it stays private instead, since it can move there (Devin on #572).
 */
function staysIn(
  title: string,
  current: AreaRef,
  canMove: boolean,
): { title: string; line: string } {
  const what = /^Which area is (this \w+) in\?$/.exec(title)?.[1] ?? "this";
  const This = `${what[0].toUpperCase()}${what.slice(1)}`;
  const share = "To share something, copy it into a chat or doc outside it yourself.";
  return canMove
    ? {
        title: `${This} stays private`,
        line: `It can move only to another private area that's just yours. ${share}`,
      }
    : {
        title: `${This} stays in ${current.label}`,
        line: `What's in a private area stays private. ${share}`,
      };
}

/** Slice 6g §4.3: in a private Area, the sheet says it stays, and offers no New area. */
export function pickerWords(
  title: string,
  line: string | null | undefined,
  current: AreaRef | null,
  newArea: boolean,
  choices: readonly Choice[],
) {
  if (!current?.private) return { words: { title, line }, offerNew: newArea };
  const canMove = choices.some((c) => c.private && c.id !== current.id && !c.shut && !c.archived);
  return { words: staysIn(title, current, canMove), offerNew: false };
}

/** Under a private Area you could move something into (P3). */
const PRIVATE_CHOICE_LINE = "Just you. No one else will see it from then on.";

function ChoiceIcon({ choice }: { choice: Choice }) {
  if (choice.icon) return <ChatIcon name={choice.icon} size={18} />;
  return <CircleDashed aria-hidden className="size-[18px] text-muted-foreground" />;
}

/** A choice's label, with its lock when it's private. */
function ChoiceLabel({ choice }: { choice: Choice }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="truncate">{choice.label}</span>
      {choice.private ? <AreaLock size={11} /> : null}
    </span>
  );
}

/** What opens the sheet: the Area's chip, or "Add to an area". */
function TriggerFace({ current }: { current: AreaRef | null }) {
  if (!current) {
    return (
      <>
        <Plus className="size-3.5" />
        Add to an area
      </>
    );
  }
  return (
    <>
      <ChatIcon name={current.icon} size={14} />
      <span className="truncate">{current.label}</span>
      {current.private ? <AreaLock size={11} /> : null}
      <ChevronDown className="size-3.5 opacity-60" />
    </>
  );
}

/** A public Area's chip is moss; a private one's is ink on beige (slice 6g §8.2). */
const triggerTone = (current: AreaRef | null) => {
  if (!current) return "text-muted-foreground";
  if (current.archived) return "text-hint";
  return current.private ? "bg-secondary text-foreground" : "text-moss";
};

const triggerClass = (current: AreaRef | null) =>
  cn(
    "inline-flex h-6 max-w-[240px] items-center md:h-7 gap-1.5 rounded-md px-2 text-meta font-medium outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
    triggerTone(current),
  );

/**
 * The Area sheet (spec §8.3; A2c): the Areas, "No area" and, with
 * `onNewArea`'s window, "New area…", which files it in the new Area once
 * made. `line` says what moves with it. A pick of the current Area does
 * nothing.
 */
export function AreaPicker({
  me,
  current,
  title,
  line,
  onPick,
  busy = false,
  error,
  newArea = false,
  className,
}: {
  me: Pick<Me, "areas">;
  current: AreaRef | null;
  /** "Which area is this chat in?" */
  title: string;
  line?: string | null;
  onPick: (areaId: string | null) => void;
  busy?: boolean;
  error?: unknown;
  /** Offer "New area…" (a chat's header). */
  newArea?: boolean;
  className?: string;
}) {
  const mobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const choices = choicesFor(me, current);
  // With no Area to pick and none to make, there's nothing to offer.
  if (me.areas.length === 0 && !current && !newArea) return null;
  const { words, offerNew } = pickerWords(title, line, current, newArea, choices);
  const pick = (id: string | null) => {
    setOpen(false);
    if (id !== (current?.id ?? null)) onPick(id);
  };
  const trigger = pickerTrigger(current, busy, mobile ? () => setOpen(true) : undefined);
  const onNew = offerNew
    ? () => {
        setOpen(false);
        setCreating(true);
      }
    : undefined;
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-2", className)}>
      {mobile ? (
        <PickerSheet
          trigger={trigger}
          open={open}
          onOpenChange={setOpen}
          title={words.title}
          line={words.line}
          choices={choices}
          current={current}
          onPick={pick}
          onNew={onNew}
        />
      ) : (
        <PickerMenu
          trigger={trigger}
          title={words.title}
          line={words.line}
          choices={choices}
          current={current}
          onPick={pick}
          onNew={onNew}
        />
      )}
      <PickError error={error} />
      {offerNew ? (
        <NewAreaWindow
          open={creating}
          onOpenChange={setCreating}
          onCreated={(area) => onPick(area.id)}
        />
      ) : null}
    </span>
  );
}

/** The picker's button: the chip, or "Add to an area". */
function pickerTrigger(current: AreaRef | null, busy: boolean, onClick?: () => void) {
  return (
    <button
      type="button"
      disabled={busy}
      data-area-picker={current?.slug ?? "none"}
      aria-label={current ? `Area: ${current.label}. Change` : "Add to an area"}
      className={triggerClass(current)}
      onClick={onClick}
    >
      <TriggerFace current={current} />
    </button>
  );
}

/** A phone's picker (A2c): the trigger, and the bottom sheet it opens. */
function PickerSheet({
  trigger,
  open,
  onOpenChange,
  title,
  line,
  choices,
  current,
  onPick,
  onNew,
}: {
  trigger: ReactElement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  line?: string | null;
  choices: Choice[];
  current: AreaRef | null;
  onPick: (id: string | null) => void;
  onNew?: () => void;
}) {
  return (
    <>
      {trigger}
      <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
        <DrawerContent showCloseButton className="pb-[env(safe-area-inset-bottom)]">
          <DrawerHeader className="pb-4">
            <DrawerTitle>{title}</DrawerTitle>
            {line ? <DrawerDescription>{line}</DrawerDescription> : null}
          </DrawerHeader>
          <SheetChoices choices={choices} current={current} onPick={onPick} onNew={onNew} />
        </DrawerContent>
      </Drawer>
    </>
  );
}

/** Why a move was refused, beside the picker. */
function PickError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <span role="alert" className="text-meta text-brick">
      {errorHint(error)}
    </span>
  );
}

/** A desktop's menu (A2c): the title, what moves, the choices as radios, then New area. */
function PickerMenu({
  trigger,
  title,
  line,
  choices,
  current,
  onPick,
  onNew,
}: {
  trigger: ReactElement;
  title: string;
  line?: string | null;
  choices: Choice[];
  current: AreaRef | null;
  onPick: (id: string | null) => void;
  onNew?: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger render={trigger} />
      <DropdownMenuContent align="start" className="w-72" data-area-menu>
        <DropdownMenuGroup>
          <DropdownMenuLabel className="text-sm font-semibold text-foreground">
            {title}
          </DropdownMenuLabel>
          {line ? (
            <p className="px-1.5 pb-1 text-xs text-muted-foreground" data-area-moves>
              {line}
            </p>
          ) : null}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={current?.id ?? "none"}
          onValueChange={(v) => onPick(v === "none" ? null : (v as string))}
        >
          {choices.map((c) => (
            <DropdownMenuRadioItem
              key={c.id ?? "none"}
              value={c.id ?? "none"}
              disabled={c.archived || c.shut}
              data-area-choice={c.label}
              className="items-start"
            >
              <ChoiceIcon choice={c} />
              <span className="flex min-w-0 flex-col">
                <ChoiceLabel choice={c} />
                {c.private && c.id !== current?.id ? (
                  <span className="text-xs text-muted-foreground">{PRIVATE_CHOICE_LINE}</span>
                ) : null}
              </span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {onNew ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onNew} data-area-new>
              <Plus />
              New area…
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** A phone's sheet (A2c): the choices on a white card, the current one checked, then New area. */
function SheetChoices({
  choices,
  current,
  onPick,
  onNew,
}: {
  choices: Choice[];
  current: AreaRef | null;
  onPick: (id: string | null) => void;
  onNew?: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 px-4 pb-4" data-area-sheet>
      <ul className="flex flex-col divide-y divide-border rounded-xl bg-card ring-1 ring-border">
        {choices.map((c) => {
          const on = (current?.id ?? null) === c.id;
          return (
            <li key={c.id ?? "none"}>
              <button
                type="button"
                aria-pressed={on}
                disabled={c.archived || c.shut}
                data-area-choice={c.label}
                onClick={() => onPick(c.id)}
                className={cn(
                  "flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-left text-body disabled:opacity-40",
                  on ? "font-medium text-foreground" : "text-copy",
                )}
              >
                <ChoiceIcon choice={c} />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <ChoiceLabel choice={c} />
                  {c.private ? (
                    <span className="text-meta font-normal text-muted-foreground">
                      {on ? "Just you" : PRIVATE_CHOICE_LINE}
                    </span>
                  ) : null}
                </span>
                {on ? <Check aria-hidden className="size-4 text-foreground" /> : null}
              </button>
            </li>
          );
        })}
      </ul>
      {onNew ? (
        <button
          type="button"
          onClick={onNew}
          data-area-new
          className="flex h-12 items-center gap-3 px-1 text-body text-foreground"
        >
          <Plus aria-hidden className="size-[18px]" />
          New area…
        </button>
      ) : null}
    </div>
  );
}
